import { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Button } from '@shared/ui/Button';
import { IconCheck } from '@shared/ui/icons';

type Decision = 'accepted' | 'disputed' | 'rejected';

interface Props {
  /** Сколько рисков выбрано. 0 = бар не показываем. */
  selectedCount: number;
  /** Сколько всего рисков на экране — для «Выбрать все». */
  totalCount: number;
  /** Все ли выбраны (для toggle-кнопки). */
  allSelected: boolean;
  /** Изменился ли выбор хоть раз (показывает «Отмена»). */
  onToggleAll: () => void;
  onCancel: () => void;
  onDecide: (d: Decision) => Promise<void> | void;
  busy?: boolean;
}

const slideUp = keyframes`
  from { transform: translateY(120%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const Bar = styled.div`
  position: fixed;
  bottom: ${({ theme }) => theme.spacing(5)};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(5)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  z-index: ${({ theme }) => theme.z.modal + 5};
  animation: ${slideUp} ${({ theme }) => theme.motion.base}
    ${({ theme }) => theme.motion.spring};
`;

const Count = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
`;

const Hotkey = styled.kbd`
  display: inline-block;
  padding: 1px 6px;
  font-size: 10px;
  font-family: ${({ theme }) => theme.font.mono};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-left: 4px;
`;

const DecisionBtn = styled(Button)<{ $tone: 'good' | 'neutral' | 'bad' }>`
  background: ${({ $tone, theme }) =>
    $tone === 'good'
      ? theme.colors.status.success
      : $tone === 'bad'
        ? theme.colors.status.danger
        : theme.colors.status.warning};
  border-color: transparent;
  color: #fff;
  &:hover:not(:disabled) {
    filter: brightness(1.1);
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 22px;
  background: ${({ theme }) => theme.colors.border};
`;

export function RiskBulkActionBar({
  selectedCount,
  totalCount,
  allSelected,
  onToggleAll,
  onCancel,
  onDecide,
  busy,
}: Props) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Не перехватываем горячие клавиши когда юрист печатает в input/textarea.
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)
      ) {
        return;
      }
      // Только без модификаторов — Ctrl/Alt/Meta не трогаем.
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const k = e.key.toLowerCase();
      if (k === 'a' && selectedCount > 0) {
        e.preventDefault();
        void onDecide('accepted');
      } else if (k === 'd' && selectedCount > 0) {
        e.preventDefault();
        void onDecide('disputed');
      } else if (k === 'r' && selectedCount > 0) {
        e.preventDefault();
        void onDecide('rejected');
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedCount, onDecide, onCancel]);

  const noneSelected = selectedCount === 0;
  return (
    <Bar role="toolbar" aria-label="Массовая разметка рисков">
      <Count>
        {noneSelected ? (
          <>Выбери риски галочками или кликом по тексту</>
        ) : (
          <>
            Выбрано <strong>{selectedCount}</strong> из {totalCount}
          </>
        )}
      </Count>
      <Button type="button" $variant="secondary" onClick={onToggleAll} disabled={busy}>
        {allSelected ? 'Снять выделение' : 'Выбрать все'}
      </Button>
      <Divider />
      <DecisionBtn
        type="button"
        $tone="good"
        onClick={() => void onDecide('accepted')}
        disabled={busy || noneSelected}
      >
        <IconCheck size={14} /> Принять
        <Hotkey>A</Hotkey>
      </DecisionBtn>
      <DecisionBtn
        type="button"
        $tone="neutral"
        onClick={() => void onDecide('disputed')}
        disabled={busy || noneSelected}
      >
        ? Под вопросом
        <Hotkey>D</Hotkey>
      </DecisionBtn>
      <DecisionBtn
        type="button"
        $tone="bad"
        onClick={() => void onDecide('rejected')}
        disabled={busy || noneSelected}
      >
        ✗ Отклонить
        <Hotkey>R</Hotkey>
      </DecisionBtn>
      <Divider />
      <Button type="button" $variant="secondary" onClick={onCancel} disabled={busy}>
        Отмена
        <Hotkey>Esc</Hotkey>
      </Button>
    </Bar>
  );
}
